import {
  ButtonContained,
  Checkbox,
  FormBox,
  FormControlLabel,
  LayoutButton,
  LayoutVertical,
  Loader,
  TextField,
  TextFieldPhoneNumber,
} from "@/components";
import useAuth from "@/hooks/useAuth";
import { useNavigationError } from "@/hooks/useNavigationError";
import { passwordValidatorForLogin, phoneNumberValidatorDelimited, validationSchema } from "@/utils/validators";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export const LoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const navigate = useNavigate();
  const { navigateError } = useNavigationError();
  const { logInByPhoneNumber } = useAuth();
  const { t } = useTranslation();
  type FormData = {
    phoneNumber: string;
    password: string;
  };

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isSubmitting },
    setError,
  } = useForm<FormData>({
    resolver: yupResolver<FormData>(
      validationSchema.shape({
        phoneNumber: phoneNumberValidatorDelimited,
        password: passwordValidatorForLogin,
      })
    ),
    defaultValues: {
      phoneNumber: "",
      password: "",
    },
    mode: "onBlur",
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      await logInByPhoneNumber(
        data.phoneNumber.replaceAll(" ", ""), // NOTE: TextFieldPhoneNumber使用するためtrim()ではなくreplaceAll()でスペース除去
        data.password
      );
      navigate("/crew_portal");
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorCode = error.response?.data.error_code;
        if (["E400-02"].includes(errorCode)) {
          // E400-02(入力値エラー)の場合は自画面エラー
          setError("phoneNumber", { type: "server", message: "" });
          setError("password", {
            type: "server",
            message: t(`ERROR_CODE_${errorCode}/api/login_MESSAGE`),
          }); // パスワード項目にてエラー文言表示
        } else {
          navigateError({
            error: error,
            endpoint: "/api/login",
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} name="loginForm">
        <LayoutVertical>
          <Controller
            name="phoneNumber"
            control={control}
            rules={{ required: t("PHONE_NUMBER_REQUIRED") }}
            render={({ field: { onChange, onBlur, value } }) => (
              <FormBox helperText={t("PHONE_NUMBER_NOTE")}>
                <TextFieldPhoneNumber
                  onChange={(e) => {
                    onChange(e);
                  }}
                  onBlur={onBlur}
                  value={value}
                  type="tel"
                  label={t("PHONE_NUMBER")}
                  fullWidth
                  error={Boolean(errors.phoneNumber)}
                  helperText={errors.phoneNumber?.message}
                />
              </FormBox>
            )}
          />

          <Controller
            name="password"
            control={control}
            rules={{ required: t("PASSWORD_REQUIRED") }}
            render={({ field: { onChange, onBlur, value } }) => (
              <FormBox>
                <TextField
                  onChange={(e) => {
                    onChange(e);
                  }}
                  onBlur={onBlur}
                  value={value}
                  type={showPassword ? "text" : "password"}
                  label={t("PASSWORD")}
                  fullWidth
                  error={Boolean(errors.password)}
                  helperText={errors.password?.message}
                  inputProps={{ maxLength: 64 }}
                />
              </FormBox>
            )}
          />
          <FormControlLabel
            control={<Checkbox checked={showPassword} onChange={handleShowPassword} name="show_password" />}
            label={t("SHOW_PASSWORD")}
          />
          {isLoading ? (
            <Loader />
          ) : (
            <LayoutButton>
              <ButtonContained size="large" type="submit" disabled={!isDirty || isSubmitting}>
                {t("LOGIN")}
              </ButtonContained>
            </LayoutButton>
          )}
        </LayoutVertical>
      </form>
    </>
  );
};
